import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Slider } from '@/components/ui/slider';
import { Minus, Info, AlertTriangle } from 'lucide-react';
import { useFirebaseFees } from '@/providers/FirebaseFeeProvider';
import { useWalletClient, useNetwork } from 'wagmi';
import { toast } from 'sonner';
import { ethers } from 'ethers';
import { getTxOptions } from '@/utils/gas';

export function RemoveLiquidity() {
  const [pairAddress, setPairAddress] = useState('');
  const [removePercentage, setRemovePercentage] = useState([50]);
  const { data: walletClient } = useWalletClient();
  const { chain } = useNetwork();
  const { settings: feeSettings, loading: feeLoading } = useFirebaseFees();

  // Get native token symbol
  const nativeSymbol = chain?.id ? {
    1: 'ETH',
    56: 'BNB',
    204: 'BNB',
    43114: 'AVAX',
    11124: 'ETH',
    97: 'BNB',
    5611: 'BNB',
    43113: 'AVAX',
    11155111: 'ETH'
  }[chain.id] || 'ETH' : 'ETH';

  // Helper function to get network fee
  const getNetworkFee = (feeType: 'removeLiquidityFee') => {
    if (!feeSettings?.networkFees || !chain?.id) return '0';
    
    // Map testnet to mainnet for fee lookup
    const mainnetChainId = {
      97: 56,        // BSC Testnet -> BSC Mainnet
      5611: 204,     // opBNB Testnet -> opBNB Mainnet
      11155111: 1,   // Sepolia -> Ethereum Mainnet
      43113: 43114,  // Fuji Testnet -> Avalanche Mainnet
      11124: 2741,   // Abstract Sepolia -> Abstract Mainnet
      421614: 42161, // Arbitrum Sepolia -> Arbitrum One
      84532: 8453,   // Base Sepolia -> Base Mainnet
      11155420: 10,  // Optimism Sepolia -> Optimism Mainnet
      80002: 137,    // Polygon Amoy -> Polygon Mainnet
      1328: 1329     // Sei Testnet -> Sei Mainnet
    }[chain.id] || chain.id;

    // ETH-based networks that should use Ethereum fees if not found
    const ethBasedNetworks = [2741, 11124, 42161, 421614, 8453, 84532, 10, 11155420];
    
    let networkFees = feeSettings.networkFees[mainnetChainId];
    
    // If no fees found and it's an ETH-based network, use Ethereum fees
    if (!networkFees && ethBasedNetworks.includes(mainnetChainId)) {
      networkFees = feeSettings.networkFees[1]; // Ethereum mainnet
    }
    
    if (!networkFees) return '0';
    
    return networkFees[feeType] || '0';
  };

  return (
    <div className="space-y-6">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Remove your liquidity from a pool to retrieve your tokens. You'll receive both tokens in the pair plus any earned fees.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Minus className="h-5 w-5 text-red-500" />
            Remove Liquidity
          </CardTitle>
          <CardDescription>
            Withdraw your tokens from a liquidity pool
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Pair Address */}
          <div className="space-y-2">
            <Label htmlFor="remove-pair-address">Liquidity Pool Address</Label>
            <Input
              id="remove-pair-address"
              placeholder="0x..."
              value={pairAddress}
              onChange={(e) => setPairAddress(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              The address of the liquidity pool you want to remove from
            </p>
          </div>

          {/* Remove Percentage Slider */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Amount to Remove</Label>
              <span className="text-2xl font-bold text-primary">{removePercentage[0]}%</span>
            </div>
            <Slider
              value={removePercentage}
              onValueChange={setRemovePercentage}
              max={100}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
            
            {/* Quick Select Buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRemovePercentage([25])}
              >
                25%
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRemovePercentage([50])}
              >
                50%
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRemovePercentage([75])}
              >
                75%
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRemovePercentage([100])}
              >
                Max
              </Button>
            </div>
          </div>

          {/* Estimated Receive */}
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <p className="text-sm font-medium mb-3">You will receive:</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Token A</span>
                  <span className="font-medium">-- Tokens</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Token B</span>
                  <span className="font-medium">-- Tokens</span>
                </div>
                <div className="flex items-center justify-between text-sm pt-2 border-t">
                  <span className="text-muted-foreground">LP Tokens to Burn</span>
                  <span className="font-medium">-- LP</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Warning for 100% removal */}
          {removePercentage[0] === 100 && (
            <Alert className="border-yellow-500/50 bg-yellow-500/10">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <AlertDescription>
                You are removing all of your liquidity. This will completely exit your position from this pool.
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button className="flex-1" size="lg" variant="destructive" disabled>
              <Minus className="mr-2 h-4 w-4" />
              Remove Liquidity
            </Button>
            <Button variant="outline" size="lg" onClick={() => {
              setPairAddress('');
              setRemovePercentage([50]);
            }}>
              Clear
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Coming soon: Full remove liquidity functionality
          </p>

          {/* Platform Fee Display */}
          {!feeLoading && feeSettings?.networkFees && chain?.id && (
            <Alert className="border-primary/50 bg-primary/10 mt-4">
              <Info className="h-4 w-4 text-primary" />
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <span>
                    <strong>Platform Fee:</strong> {getNetworkFee('removeLiquidityFee')}% of {nativeSymbol} amount
                  </span>
                  <span className="text-xs text-muted-foreground">
                    (Collected when removing liquidity)
                  </span>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
